'use client'

import * as React from 'react'
import { TrendingDown } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { isModelDegraded, type AiModelTier } from '@/lib/ai/credit-state'
import { useTranslation } from '@/lib/i18n'
import { cn } from '@/lib/utils'

/**
 * "This ran in reduced mode."
 *
 * ── Why this component is not optional and not dismissible ──────────────────
 * A silent PRO→MINI downgrade shipped once and nobody noticed. The output kept
 * arriving, it was just worse, and no surface said so — operators judged the
 * feature on degraded results and had no way to know. A degraded run MUST say
 * so. That is the rule this component exists to enforce.
 *
 * Consequences, both deliberate:
 *
 *  1. **No dismiss control.** The condition is not an announcement, it is the
 *     state of the result sitting next to it. A dismiss button would let the
 *     notice be cleared while the degraded output stays on screen — which is
 *     the silent downgrade again, one click later. Contrast
 *     `components/shared/launch-drops-banner.tsx`, which IS dismissible: that
 *     one reports a completed event whose record persists server-side.
 *
 *  2. **Driven by `modelTier`, not by the credit state.** `resolveCreditState`
 *     escalates along a severity ladder, so an allowance that is both degraded
 *     and at 85% resolves to `warning` and the `'degraded'` literal is gone.
 *     Reading the tier directly means the notice survives that. See the
 *     SEVERITY comment in `lib/ai/credit-state.ts`.
 *
 * Tone follows `app/play/library/_components/round-detail-sheet.tsx:322-326`
 * ("Basic assessment — detailed AI analysis was not available for this
 * round."): state the limitation plainly, do not apologise, do not explain the
 * machinery. No tier code, model name or provider appears in the copy, in any
 * language: lib/ai/__tests__/quota-copy.spec.ts holds the catalogue to that.
 */
export interface AiDegradedNoticeProps {
  modelTier: AiModelTier
  className?: string
}

export function AiDegradedNotice({
  modelTier,
  className,
}: AiDegradedNoticeProps) {
  const { t } = useTranslation()
  // `served: null` means nothing has been served yet — not a downgrade.
  if (!isModelDegraded(modelTier)) return null

  return (
    <Alert
      variant="info"
      data-testid="ai-degraded-notice"
      className={cn(className)}
    >
      <TrendingDown aria-hidden="true" />
      <AlertTitle>{t('aiDegradedNotice.headline')}</AlertTitle>
      <AlertDescription>{t('aiDegradedNotice.body')}</AlertDescription>
    </Alert>
  )
}
